import PropTypes from "prop-types";
import { Drawer, Typography, Menu, Button, Input } from "@/config/antd";
import { NavIcons } from "@/config/antd";
import { CloseOutlined, SearchOutlined } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import "@/assets/styles/slide_out_menu.css";
import { useSelector } from "react-redux";
import {
  selectUserProfile,
  selectIsSearching,
} from "@/store/redux/slices/user/user_slice";
import { useTheme } from "@/hooks/ThemeContext";

const { Text } = Typography;

const NavIcon = ({ iconName, state }) => {
  const { theme } = useTheme();

  // In dark mode, always use default state
  const iconState = theme === "dark" ? "default" : state;

  return (
    <img
      src={
        iconState === "hover"
          ? NavIcons[iconName].hover
          : NavIcons[iconName].default
      }
      alt={`${iconName} icon`}
      className={`w-8 h-8 nav-icon ${theme === "dark" ? "dark-mode-icon" : ""}`}
    />
  );
};

NavIcon.propTypes = {
  iconName: PropTypes.oneOf([
    "Campaign",
    "Candidates",
    "Canvass",
    "Finance",
    "Help",
    "Home",
    "Manifesto",
    "Media",
    "Polls",
    "Roles",
    "Settings",
    "Support",
    "Townhall",
    "Users",
  ]),
  state: PropTypes.oneOf(["default", "hover"]),
};

const createMenuItems = () => {
  const createItem = (key, iconName, label) => ({
    key,
    icon: <NavIcon iconName={iconName} state="default" />,
    label: <span className="text-medium">{label}</span>,
  });

  return [
    createItem("", "Home", "Overview"), // Empty key for root dashboard URL
    {
      key: "management",
      type: "group",
      label: <span className="text-medium font-bold">Management</span>,
      children: [
        createItem("events", "Campaign", "Events"),
        createItem("candidates", "Candidates", "Candidates"),
        createItem("canvass", "Canvass", "Canvass"),
        createItem("townhall", "Townhall", "Townhall"),
        createItem("manifesto", "Manifesto", "Manifesto"),
        createItem("polls", "Polls", "Polls and Surveys"),
        createItem("media", "Media", "Media Manager"),
        createItem("financial", "Finance", "Financial Manager"),
      ],
    },
    {
      key: "administrative",
      type: "group",
      label: <span className="text-medium font-bold">Administration</span>,
      children: [
        createItem("users", "Users", "Team Admin"),
        createItem("roles", "Roles", "Roles Management"),
        createItem("settings", "Settings", "Settings"),
        createItem("settings/support", "Support", "Support"),
        createItem("settings/help/faq/support", "Help", "Help and FAQ"),
      ],
    },
  ];
};

export const SlideOutMenu = ({
  visible = false,
  onClose = () => {},
  onMenuClick = () => {},
}) => {
  const userProfile = useSelector(selectUserProfile);
  const isSearching = useSelector(selectIsSearching);
  const menuItems = createMenuItems();

  const handleMenuClick = ({ key }) => {
    onMenuClick(key === "" ? "" : key); // Handle the empty key case
  };

  return (
    <>
      <div className="hidden"></div>
      <Drawer
        placement="left"
        onClose={onClose}
        open={visible}
        width="100%"
        height="100%"
        styles={{
          body: { padding: 0 },
          wrapper: { position: "absolute" },
          content: {
            boxShadow: "none",
          },
          mask: {
            backgroundColor: "rgba(0, 0, 0, 0.45)",
          },
        }}
        closeIcon={false}
        className="w-full custom-drawer md:w-4/5 lg:w-1/2">
        <div className="mobile-menu-container flex flex-col h-full">
          <div className="p-6 pb-0 mobile-menu-header">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Avatar
                  size={64}
                  src={userProfile.profileImage || undefined}
                  className="mr-4"
                />
                <div className="flex flex-col">
                  <Text className="text-small mobile-welcome-text">
                    Welcome
                  </Text>
                  <Text className="m-0 font-semibold text-bold-x mobile-username truncate max-w-[150px] mx-auto">
                    {isSearching ? "Loading..." : userProfile.fullname}
                  </Text>
                </div>
              </div>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={onClose}
                className="text-xl close-button mobile-close-btn"
              />
            </div>
            <div className="relative mb-4">
              <Input
                placeholder="Search..."
                suffix={<SearchOutlined className="mobile-search-icon" />}
                className="py-3 pl-5 rounded-full mobile-search pr-11 border-primary"
              />
            </div>
          </div>
          <Menu
            mode="inline"
            items={menuItems}
            onClick={handleMenuClick}
            className="flex-grow overflow-y-auto border-r-0 custom-menu mobile-menu"
          />
        </div>
      </Drawer>
    </>
  );
};

SlideOutMenu.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onMenuClick: PropTypes.func,
};
